import { Button, Text } from "north.js";
import React from "react";
import { BottomSheet } from "react-spring-bottom-sheet";

import "react-spring-bottom-sheet/dist/style.css";
import styled from "styled-components";

import { Icon } from "src/shared/components";
import globalConstants from "src/theme/globalConstants";

interface HardwareResultBottomSheetProps {
  isOpen: boolean;
  isCheckout: boolean;
  result: { success: boolean; error?: string; data?: any };
  setShowResultModal: (showResultModal: boolean) => void;
}

const HardwareResultBottomSheet: React.FC<HardwareResultBottomSheetProps> = ({
  isOpen,
  isCheckout,
  result,
  setShowResultModal,
}) => {
  const actionTextCapitalized = isCheckout ? "Checkout" : "Return";

  return (
    <StyledBottomSheet
      open={isOpen}
      onDismiss={() => setShowResultModal(false)}
      // without this, the HasIdConfirmationModal is blocked
      blocking={false}
    >
      <ModalWrapper>
        <ResultIcon>
          {result.success ? (
            <Icon name="check-circle-inverted-green" size="title" />
          ) : (
            <Icon name="check-circle-inverted-red" size="title" />
          )}
        </ResultIcon>

        <ResultText>
          <Text type="h1">
            {result.success
              ? `${actionTextCapitalized} Successful`
              : `${actionTextCapitalized} Failed`}
          </Text>
          <Text
            type="body1"
            style={{
              color: result.success
                ? (globalConstants?.color?.textSuccess as string)
                : (globalConstants?.color?.textDanger as string),
            }}
          >
            {result.success
              ? `Hardware request has been marked as ${
                  isCheckout ? "picked up" : "returned"
                }`
              : result.error || "An unexpected error occurred"}
          </Text>
        </ResultText>

        {result.success && result.data && (
          <RequestDetails>
            <Text type="h3">Request Details</Text>
            <DetailRow>
              <Text type="body2">Request ID:</Text>
              <Text type="body2" style={{ fontWeight: 600 }}>
                #
                {result.data.markHardwareRequestAsPickedUp?.id ||
                  result.data.returnHackerHardwareRequest?.id}
              </Text>
            </DetailRow>
            <DetailRow>
              <Text type="body2">Status:</Text>
              <Text type="body2" style={{ fontWeight: 600 }}>
                {result.data.markHardwareRequestAsPickedUp?.status ||
                  result.data.returnHackerHardwareRequest?.status}
              </Text>
            </DetailRow>
            {result.data.markHardwareRequestAsPickedUp?.user && (
              <DetailRow>
                <Text type="body2">User:</Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {result.data.markHardwareRequestAsPickedUp.user.name}
                </Text>
              </DetailRow>
            )}
            {result.data.returnHackerHardwareRequest?.user && (
              <DetailRow>
                <Text type="body2">User:</Text>
                <Text type="body2" style={{ fontWeight: 600 }}>
                  {result.data.returnHackerHardwareRequest.user.name}
                </Text>
              </DetailRow>
            )}
          </RequestDetails>
        )}

        <ModalButtons>
          <Button
            color="primary"
            size="md"
            onClick={() => setShowResultModal(false)}
          >
            Done
          </Button>
        </ModalButtons>
      </ModalWrapper>
    </StyledBottomSheet>
  );
};

export default HardwareResultBottomSheet;

const StyledBottomSheet = styled(BottomSheet)`
  position: fixed;
  z-index: 10000 !important;
`;

const ModalWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
`;

const ResultIcon = styled.div`
  margin-bottom: 24px;
`;

const ResultText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 32px;
  text-align: center;
`;

const RequestDetails = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 32px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  width: 100%;
`;
